import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { parseISO } from 'date-fns'

/**
 * The three states an upcoming visit can be in, which the API's two booleans describe between
 * them. The third — booked far enough out that nobody has been asked yet — is the one with no
 * flag of its own, and the one the app used to draw as nothing at all.
 */
export type ConfirmState = 'confirmed' | 'open' | 'early'

export const confirmStateOf = (visit: UpcomingVisit): ConfirmState =>
	visit.isConfirmed ? 'confirmed' : visit.isConfirmable ? 'open' : 'early'

/**
 * The instant the studio will start asking.
 *
 * The server's, not a subtraction of our own. provider-kapi computes it beside `isConfirmable`
 * from one expression, so the date this card names and the moment the button appears cannot
 * drift apart — and if the window ever moves, the app follows without a release.
 */
export const confirmWindowOpensAt = (visit: UpcomingVisit) =>
	parseISO(visit.confirmationOpensAt)

/**
 * Whether to tell a member when they will be asked to confirm.
 *
 * Only while the asking is still ahead of them. A visit whose window has opened has a Confirm
 * button instead, and one booked inside the window was confirmed at the moment it was booked
 * and will never be asked at all — in both of those, a line saying when confirmation opens is
 * either contradicted by the button beside it or describes something that already happened.
 *
 * The instant is checked as well as the state. `early` should already mean the window is ahead,
 * but the flag and the date are computed on the server against its clock and read here against
 * the phone's, and the one case that must not slip through is telling someone to come back on a
 * day that has passed.
 */
export const shouldSayWhenConfirmOpens = (visit: UpcomingVisit, now: Date) =>
	confirmStateOf(visit) === 'early' && confirmWindowOpensAt(visit) > now

/**
 * How long before a visit something opens, in words, counted from the server's own two instants.
 *
 * The window is never named as a number here. "Four days" is `startTimeUtc` minus
 * `confirmationOpensAt`, and thirty minutes is `startTimeUtc` minus `checkIn.opensAt` — so the
 * copy tells a member what the server is actually doing, and moves with it if the window ever
 * changes. Writing "4 days" as a literal would put the constant back in the app, which is the
 * thing `confirmationOpensAt` was added to stop.
 */
export const leadTimeLabel = (startTimeUtc: string, opensAt: string) => {
	/* Minutes, then hours, then days, and the boundaries are where the smaller unit stops being
	   the more useful one: under an hour a member is counting minutes, and at a day exactly they
	   are owed "1 day" rather than "24 hours". */
	const minutes = Math.round((Date.parse(startTimeUtc) - Date.parse(opensAt)) / 60_000)

	// Abbreviated, as the countdown beside it is — "in 25 mins" — and because the only window
	// measured in minutes is the door's, where the line reads "Check-in opens 30 mins before".
	// Hours and days stay spelled: they turn up in "Confirm 4 days before your visit", which is
	// a sentence rather than a clock.
	if (minutes < 60) {
		return `${minutes} min${minutes === 1 ? '' : 's'}`
	}
	if (minutes < 60 * 24) {
		const hours = Math.round(minutes / 60)
		return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
	}
	const days = Math.round(minutes / (60 * 24))

	return `${days} ${days === 1 ? 'day' : 'days'}`
}

/**
 * "Confirm 4 days before your visit."
 *
 * Relative, not a date. A member reading this three weeks out is not going to hold "Fri, Sep 25"
 * in their head — and a date is a thing they then have to check against a calendar, where "four
 * days before" is a rule they already understand. It also puts this line in the same shape as
 * the check-in one beside it, which has always read "opens 30 mins before".
 *
 * It opened with "You can confirm", which is three words spent saying that a thing is permitted
 * before the line gets to when. Under a button labelled Confirm, the subject is already on the
 * screen: this is the instruction that button is waiting for, in the same voice the button uses.
 */
export const confirmOpensLabel = (visit: UpcomingVisit) =>
	`Confirm ${leadTimeLabel(
		visit.startTimeUtc,
		visit.confirmationOpensAt
	)} before your visit.`

/*
 * Home's card still counts the window rather than naming the day, and the visit page names it.
 *
 * Not an inconsistency waiting to be tidied. The card's line is under a button in a stack of
 * four tiles, where "Confirm 4 days before your visit" is a rule of the service said once; the
 * page has a row with a sentence in it, and a member who has opened a page about one visit is
 * asking about that visit, where a date beats a duration. `visitStateRow` builds the date from
 * the same `confirmationOpensAt` this counts from, so the two cannot disagree about the facts —
 * only about how much detail the surface is worth.
 */

/**
 * The chip says the state; the line beneath it says the timing.
 *
 * It used to say "Confirm Sep 25", which is a date doing a status's job in a space too small for
 * either. What a member wants from a mark in the corner is whether anything is outstanding.
 */
export const confirmOpensShort = () => 'Not Confirmed'
